import {onCLS, onFCP, onFID, onINP, onLCP, onTTFB} from "web-vitals";

import type {PagePerformanceCapturedV4} from "@khanacademy/event-schemas";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {PermissionsInfo} from "@khan/shared-core";
import type {Entrypoint} from "@khan/shared-core/context";

import {getTimingSummaryProp} from "./page-lifecycle.ts";

const defaultPagePerfData: PagePerformanceCapturedV4 = {
    eventType: "PagePerformanceCaptured",

    eventSchemaVersion: 4,

    // Cumulative Layout Shift (CLS) Web Vital score for the page.
    webVitalsCLS: null,

    // First Input Delay (FID) Web Vital score for the page.
    webVitalsFID: null,

    // Largest Contentful Paint (LCP) Web Vital score for the page.
    webVitalsLCP: null,

    // First Contentful Paint (FCP) Web Vital score for the page.
    webVitalsFCP: null,

    // Time to First Byte (TTFB) Web Vital score for the page.
    webVitalsTTFB: null,

    // Interaction to Next Paint (INP) Web Vital score for the page.
    webVitalsINP: null,

    // The name of the page, as defined in the page's page load metric.
    pageLoadPageName: null,

    // How long the page took to become sufficiently usable, as defined in the page's page load metric.
    pageLoadSufficientlyUsable: null,

    // How long the page took to become fully interactive, as defined in the page's page load metric.
    pageLoadFullyInteractive: null,

    // How long the page took to load the first byte.
    pageLoadFirstByte: null,

    // The path of the page, as coming from React Router (e.g. /:domain/:course).
    routerPath: "",

    // Was this page server-side rendered?
    wasServerSideRendered: false,

    // Whether the page is loaded by a phantom user, or not.
    isPhantom: false,

    // The product context of the page.
    productContext: null,
};

let pagePerfData: PagePerformanceCapturedV4 = {
    ...defaultPagePerfData,
};

// We only capture the page perf data once per page load.
let hasBeenCaptured = false;

/**
 * Get the page load metrics for the current page.
 *
 * @returns {?Object} the page load metrics, if it exists.
 */
const getTimingSummary: () => {
    pageName?: string;
    sufficientlyUsable?: number;
    fullyInteractive?: number;
    firstByte?: number;
} = () => {
    return window[getTimingSummaryProp()] || {};
};

/**
 * Reset the page performance data to its default values, for testing purposes.
 */
export const resetPagePerfData = () => {
    pagePerfData = {...defaultPagePerfData};
    hasBeenCaptured = false;
};

/**
 * Get the page performance data, for testing purposes.
 *
 * @returns {PagePerformanceCapturedV4} the page performance data.
 */
export const getPagePerfData = (): PagePerformanceCapturedV4 => pagePerfData;

/**
 * Sometimes we get bad values back from Page Lifecycle or Web Vitals. This
 * ensures that we don't try to store those values (numbers that are too low
 * or too high get converted into `null`).
 */
const ensureGoodNumber = (
    value: string | number | null | undefined,
): number | null => {
    if (!value) {
        return null;
    }
    if (typeof value === "string") {
        value = parseFloat(value);
    }
    if (value <= 0 || value > 1000000) {
        return null;
    }
    return value;
};

/**
 * Track Web Vitals for the page. Should be called from site-init
 */
export const trackWebVitals = () => {
    // Largest Contentful Paint (LCP) Web Vital score for the page.
    onLCP(({value}) => {
        pagePerfData.webVitalsLCP = ensureGoodNumber(value);
    });

    // First Contentful Paint (LCP) Web Vital score for the page.
    onFCP(({value}) => {
        pagePerfData.webVitalsFCP = ensureGoodNumber(value);
    });

    // Cumulative Layout Shift (CLS) Web Vital score for the page.
    onCLS(({value}) => {
        pagePerfData.webVitalsCLS = ensureGoodNumber(value);
    });

    // First Input Delay (FID) Web Vital score for the page.
    onFID(({value}) => {
        pagePerfData.webVitalsFID = ensureGoodNumber(value);
    });

    // Interaction to Next Paint (INP) Web Vital score for the page.
    onINP(({value}) => {
        pagePerfData.webVitalsINP = ensureGoodNumber(value);
    });

    // Time to First Byte (TTFB) Web Vital score for the page.
    onTTFB(({value}) => {
        pagePerfData.webVitalsTTFB = ensureGoodNumber(value);
    });
};

/**
 * Capture the page performance data and send it to CEDAR.
 *
 * @param {*} publishAnalyticsEvent the function to use to publish the event to CEDAR.
 * @param {string} routerPath the path of the page, as coming from React Router (e.g. /:domain/:course).
 * @param {PagePerformanceCapturedV4ProductContext} productContext the product context of the page.
 * @returns
 */
export const capturePagePerformance = (
    routerPath: string,
    productContext: Entrypoint | null = null,
) => {
    if (hasBeenCaptured) {
        return;
    }

    // Track the page marker data.
    const pageLoadData = getTimingSummary();
    if (pageLoadData) {
        const {pageName, sufficientlyUsable, fullyInteractive, firstByte} =
            pageLoadData;

        pagePerfData.pageLoadPageName = pageName || null;
        pagePerfData.pageLoadSufficientlyUsable =
            ensureGoodNumber(sufficientlyUsable);
        pagePerfData.pageLoadFullyInteractive =
            ensureGoodNumber(fullyInteractive);
        pagePerfData.pageLoadFirstByte = ensureGoodNumber(firstByte);
    }

    // Track the router path
    pagePerfData.routerPath = routerPath;

    // Determine if the page was SSRed (and was hydrated as a result)
    pagePerfData.wasServerSideRendered = false;

    // Track the user state
    pagePerfData.isPhantom = PermissionsInfo.isPhantom;

    // Track the product context
    pagePerfData.productContext = productContext;

    // Store the data in CEDAR
    publishAnalyticsEventForMinorConversion(pagePerfData);

    // Make sure we don't double-log
    hasBeenCaptured = true;
};
